import { DocsAnswer, DocsAnswerSource } from '../types';
import { IconType } from 'components/common/icon/IconHandler';

/** A Concierge panel domain as far as these helpers need it: tool name plus the admin-set Display Name and icon. */
export interface SectionDomain {
    name: string;
    title?: string;
    icon_ind?: string;
    icon_name?: string;
    icon_data?: string;
}

export interface SectionHeader {
    title: string;
    icons: IconType[];
}

/**
 * Concierge index names (IndexInfo in the Concierge service) → the panel's tool/domain names, whose
 * `title` and icon are what an admin set in Concierge Setup for that documentation block.
 */
export const DOCS_SOURCE_DOMAIN: Record<string, string> = {
    'mi-docs': 'docs',
    faq: 'faq',
    'glossary-terms': 'glossary',
};

/**
 * The documentation-based answer an additional-data row may carry (search_glossary_result, written
 * from /api/v2/answer-with-docs). Only an in-scope answer is worth a section: an out-of-scope row
 * holds the configured "no answer in the docs" sentence, an older Concierge build stores the plain
 * streamed text instead of the JSON object, and a failed call stores {status:'ERROR'} — every one
 * of those degrades to the answer-only view.
 */
export function docsAnswerFromColumn(raw: unknown): DocsAnswer | null {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

    const row = raw as { answer?: unknown; out_of_scope?: unknown; data_sources?: unknown; served_as_answer?: unknown };
    if (row.out_of_scope !== false) return null;

    const answer = typeof row.answer === 'string' ? row.answer.trim() : '';
    const dataSources = (Array.isArray(row.data_sources) ? row.data_sources : []).filter(
        (source): source is DocsAnswerSource =>
            !!source && typeof source === 'object' && typeof (source as { label?: unknown }).label === 'string',
    );

    // The question-intent design (2026-09-21, cut 7): the kind door served this answer as the chat text. Cut S
    // (2026-09-22, D4473): the row is then what the answer was read from and nothing else, so the panel draws its
    // sources and never repeats the text. A served row that names no source has nothing to draw.
    if (row.served_as_answer === true) {
        return dataSources.length ? { data_type: 'docs_answer', answer: '', data_sources: dataSources } : null;
    }
    if (!answer) return null;

    return { data_type: 'docs_answer', answer, data_sources: dataSources };
}

/** The icon an admin configured for a Concierge block, in the shape IconHandler renders (same rule as the tool chips). */
export function domainIcon(domain: SectionDomain | undefined): IconType | null {
    if (!domain?.icon_ind) return null;
    const value =
        domain.icon_ind === 'upload'
            ? domain.icon_data
                ? `data:image/png;base64,${domain.icon_data}`
                : ''
            : domain.icon_name;
    return value ? { type: domain.icon_ind as IconType['type'], value } : null;
}

/** Header of a section that maps to one Concierge block (content search): its Display Name and icon, else the fallback title. */
export function domainSectionHeader(
    domainName: string,
    domains: SectionDomain[],
    fallbackTitle: string,
): SectionHeader {
    const domain = domains.find((d) => d.name === domainName);
    const icon = domainIcon(domain);
    return { title: domain?.title || fallbackTitle, icons: icon ? [icon] : [] };
}

/**
 * Header of the documentation answer: one answer is written over every enabled documentation index,
 * so the title names each block that contributed (Display Name when the panel knows the block, the
 * service's own label otherwise) and carries each block's icon.
 */
export function docsSectionHeader(
    sources: DocsAnswerSource[],
    domains: SectionDomain[],
    fallbackTitle: string,
): SectionHeader {
    const titles: string[] = [];
    const icons: IconType[] = [];
    sources.forEach((source) => {
        const domain = domains.find((d) => d.name === DOCS_SOURCE_DOMAIN[source.name]);
        const title = domain?.title || source.label;
        if (title && !titles.includes(title)) titles.push(title);
        const icon = domainIcon(domain);
        if (icon && !icons.some((known) => known.type === icon.type && known.value === icon.value)) icons.push(icon);
    });
    return { title: titles.join(', ') || fallbackTitle, icons };
}
